<template>
  <div v-if="show" class="calendar_container">
    <div class="title_close">
      <h3 style="color: var(--titulo);">{{ title }}:</h3>
      <div style="display: flex; gap: 1rem;">
        <AppButton
          v-bind:visibility="true"
          style="background-color: var(--button); color: var(--label);"
          text="Salvar"
          emitType="select"
          @select="saveDate"
          :width="20"
          :height="20"
        />
        <AppButton
        style="color: var(--icon);"
          v-bind:visibility="true"
          left_icon="fa-regular fa-circle-xmark fa-xl"
          emitType="close"
          @close="close"
          :width="20"
          :height="20"
        />
      </div>
    </div>

    <div class="component_container w-500px h-300px">
      <DatePicker
        :initial-page="{ month: 1, year: currentYear }"
        :rows="3"
        :columns="4"
        :disabled-dates="disabledDates"
       
        :attributes="[...attributes, ...(partialSelectionAttr ? [partialSelectionAttr] : [])]"
        v-model:range="range"
        mode="range"
        @dayclick="onClick"
      />
    </div>
  </div>
</template>

<script setup>
/* ------------------------------------------------------------------
   1) IMPORTS
------------------------------------------------------------------ */
import { ref, computed,  watch } from 'vue'
import { DatePicker } from 'v-calendar'
import AppButton from '@/components/updated/button.vue'

/* ------------------------------------------------------------------
   2) PROPS & EMITS
------------------------------------------------------------------ */
const props = defineProps({
  dates: Array,
  show: Boolean,
  title: String
})

const emit = defineEmits(['save', 'close'])

/* ------------------------------------------------------------------
   3) REFS (STATE)
------------------------------------------------------------------ */
const currentYear = new Date().getFullYear()

// Local copy of parent's dates
const date_copy = ref([...props.dates])

// V-Calendar's "range" object
const range = ref({})

// If first-click is a tip of an existing date => true
const isTip = ref(false)

// Store "terminado" or other disabled dates
const invalid_dates = ref([
  { start: new Date('1900/01/01'), end: new Date() }
])

// Transformed array for v-calendar's "disabledDates"
const disabledDates = ref(
  invalid_dates.value.map(dateObj => ({
    start: dateObj.start,
    end: dateObj.end
  }))
)

/* ------------------------------------------------------------------
   4) WATCHERS
------------------------------------------------------------------ */
watch(
  () => props.dates,
  (newDates) => {
    date_copy.value = [...newDates]
    // mark "terminado" as disabled
    newDates.forEach((dateObj) => {
      if (dateObj.status === 'terminado') {
        invalid_dates.value.push({
          start: dateObj.start,
          end: dateObj.end
        })
      }
    })
    disabledDates.value = invalid_dates.value.map(dateObj => ({
      start: dateObj.start,
      end: dateObj.end
    }))
  },
  { immediate: true }
)

/* ------------------------------------------------------------------
   5) NORMAL ATTRIBUTES (existing dates)
------------------------------------------------------------------ */
const attributes = ref([])

function setHighlightColor(status) {
  switch (status) {
    case 'aprovado':
      return 'green'
    case 'pendente':
      return 'red'
    case 'terminado':
      return 'gray'
    case 'novo':
      return 'red'
    default:
      return 'blue' // default color
  }
}

function buildAttributes(arr) {
  return arr
    .filter((dateObj) => dateObj.status !== 'deleted')
    .map((dateObj) => ({
      highlight: setHighlightColor(dateObj.status),
      dates: {
        start: dateObj.start,
        end: dateObj.end
      }
    }))
}

// Initialize attributes
attributes.value = buildAttributes(date_copy.value)

// Watch date_copy for changes and rebuild
watch(
  () => date_copy.value,
  (newVal) => {
    attributes.value = buildAttributes(newVal)
  },
  { deep: true }
)

/* ------------------------------------------------------------------
   6) IN-PROGRESS BLUE ATTRIBUTE (only for "new" range, not tip)
------------------------------------------------------------------ */
const partialSelectionAttr = computed(() => {
  // If we haven't started a new range, or if it's a "tip" scenario, no highlight
  if (!range.value.start || isTip.value) {
    return null
  }
  
  // If start is defined but no end yet, we highlight from start to "hovered" day
  // V-Calendar will show partial selection from start -> hovered date in "blue"
  const start = range.value.start
  const end = range.value.end || range.value.start

  return {
    // unique key
    key: 'partial-selection',
    highlight: {
      fillMode: 'light', // can be 'light' or 'solid'
      color: 'blue'
    },
    dates: { start, end }
  }
})

/* ------------------------------------------------------------------
   7) CLICK HANDLER
------------------------------------------------------------------ */
const onClick = (clickedDate) => {
  const clickedTime = clickedDate.date.getTime()

  // 1) If range start is not set, this is the first click
  if (!range.value.start) {
    let foundDateObj
    // Check if user clicked on a tip of existing range
    isTip.value = date_copy.value.some((obj) => {
      const st = obj.start.getTime()
      const en = obj.end.getTime()
      const active = obj.status !== 'terminado' && obj.status !== 'deleted'
      if (active && (st === clickedTime || en === clickedTime)) {
        foundDateObj = obj
        return true
      }
      return false
    })

    if (isTip.value && foundDateObj) {
      // If tip => store entire date in range => next click can delete
      range.value.start = foundDateObj.start
      range.value.end = foundDateObj.end
    } else {
      // If new => only set "start"
      range.value.start = clickedDate.date
      range.value.end = null
    }
  } 
  else {
    // 2) If we have a start but no end, second click sets the end
    if (!range.value.end) {
      range.value.end = clickedDate.date
      // Swap if user clicked a day before the start
      if (range.value.end < range.value.start) {
        const tmp = range.value.start
        range.value.start = range.value.end
        range.value.end = tmp
      }
      // If it's not a tip => create a new "novo" date
      if (!isTip.value) {
        date_copy.value.push({
          start: range.value.start,
          end: range.value.end,
          status: 'novo',
          id: 0
        })
      }
      // Reset
      range.value = {}
      isTip.value = false
      return
    }
    // 3) If we already have both start & end => likely removing
    if (isTip.value) {
      // Mark that date as "deleted"
      date_copy.value = date_copy.value.map((obj) => {
        const st = obj.start.getTime()
        const en = obj.end.getTime()
        const rs = range.value.start.getTime()
        const re = range.value.end.getTime()
        if (obj.status !== 'terminado' && st === rs && en === re) {
          return { ...obj, status: 'deleted' }
        }
        return obj
      })
      // Reset
      range.value = {}
      isTip.value = false
    } else {
      // Possibly the user is picking *another* brand-new range
      date_copy.value.push({
        start: range.value.start,
        end: range.value.end,
        status: 'novo',
        id: 0
      })
      range.value = {}
    }
  }
}

/* ------------------------------------------------------------------
   8) SAVE & CLOSE
------------------------------------------------------------------ */
const saveDate = () => {
  const return_date = date_copy.value.filter(
    (obj) => !(obj.id === 0 && obj.status === 'deleted')
  )
  // Convert 'novo' to 'pendente'
  return_date.forEach((obj) => {
    if (obj.status === 'novo') {
      obj.status = 'pendente'
    }
  })
  emit('save', return_date)
}

const close = () => {
  emit('close')
}
</script>

<style scoped>
.calendar_container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-radius: 10px;
  background-color: var(--fundoForm);
}
.title_close {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4rem;
}
.component_container {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
